import { CLIENT_PREFIX, COMPANY_PREFIX, ROUTE_PATHS } from "./RoutesPaths";
import { getSessionPortal } from "./Auth";

/** Auth portal context — admin shell vs company shell (routes, API, session). */
export const AUTH_CONTEXT_TYPE = Object.freeze({
  ADMIN: "admin",
  COMPANY: "company",
  CLIENT: "client",
});

const normalizePathname = (pathname) =>
  typeof pathname === "string" ? pathname.replace(/\/+/g, "/") : "";

const isCompanyPath = (pathname) => {
  const path = normalizePathname(pathname);
  return path === COMPANY_PREFIX || path.startsWith(`${COMPANY_PREFIX}/`);
};

const isClientPath = (pathname) => {
  const path = normalizePathname(pathname);
  return path === CLIENT_PREFIX || path.startsWith(`${CLIENT_PREFIX}/`);
};

/**
 * Login route after manual or automatic logout.
 * Prefers the active portal session, then the current URL prefix (`/company`, `/client`, `/admin`).
 */
export function resolvePortalLoginPath({
  sessionPortal = getSessionPortal(),
  pathname = "",
} = {}) {
  if (sessionPortal === AUTH_CONTEXT_TYPE.COMPANY || isCompanyPath(pathname)) {
    return ROUTE_PATHS.companyLogin;
  }
  if (sessionPortal === AUTH_CONTEXT_TYPE.CLIENT || isClientPath(pathname)) {
    return ROUTE_PATHS.clientLogin;
  }
  return ROUTE_PATHS.login;
}

/**
 * Public auth/onboarding URLs under `/company` (no company app shell).
 * Everything else under `/company/*` is treated as the private company portal app.
 */
export function isCompanyPublicAuthPath(pathname) {
  if (typeof pathname !== "string") return false;
  const p = pathname.replace(/\/+/g, "/");
  if (p === COMPANY_PREFIX || p === `${COMPANY_PREFIX}/`) return true;
  if (p.startsWith(`${COMPANY_PREFIX}/login`)) return true;
  if (p.startsWith(`${COMPANY_PREFIX}/signup`)) return true;
  if (p === `${COMPANY_PREFIX}/pending-approval`) return true;
  if (p.startsWith(`${COMPANY_PREFIX}/reset-password`)) return true;
  if (p === `${COMPANY_PREFIX}/forget-password`) return true;
  if (p === `${COMPANY_PREFIX}/change-password`) return true;
  return false;
}

export function isCompanyPortalAppPath(pathname) {
  return (
    typeof pathname === "string" &&
    pathname.startsWith(`${COMPANY_PREFIX}/`) &&
    !isCompanyPublicAuthPath(pathname)
  );
}

export function isClientCompanySelectionPath(pathname) {
  if (typeof pathname !== "string") return false;
  const p = pathname.replace(/\/+/g, "/");
  return p === ROUTE_PATHS.clientSelectCompany;
}

export function isClientPublicAuthPath(pathname) {
  if (typeof pathname !== "string") return false;
  const p = pathname.replace(/\/+/g, "/");
  if (p === CLIENT_PREFIX || p === `${CLIENT_PREFIX}/`) return true;
  if (p.startsWith(`${CLIENT_PREFIX}/login`)) return true;
  if (p.startsWith(`${CLIENT_PREFIX}/reset-password`)) return true;
  if (p === `${CLIENT_PREFIX}/forget-password`) return true;
  if (p === `${CLIENT_PREFIX}/change-password`) return true;
  return false;
}

export function isClientPortalAppPath(pathname) {
  return (
    typeof pathname === "string" &&
    pathname.startsWith(`${CLIENT_PREFIX}/`) &&
    !isClientPublicAuthPath(pathname)
  );
}

/** Exact `/client/reset-password` only (not `/success`, etc.). */
export function isClientResetPasswordFlowBasePath(pathname) {
  if (typeof pathname !== "string") return false;
  const collapsed = pathname.replace(/\/+/g, "/");
  const p =
    collapsed.length > 1 && collapsed.endsWith("/")
      ? collapsed.slice(0, -1)
      : collapsed;
  return p === ROUTE_PATHS.clientResetPassword;
}
